import useMapbox from '@/composables/useMapbox'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useMapboxFeatureState } from './mapboxFeatureState'
import { getActiveLayers } from './mapboxFeatureState/getActiveLayers'

type HoverState = {
  color?: string
  opacity?: number
}

export const useHoverStore = defineStore('hover', () => {
  const { map } = useMapbox()
  const hoveredItems = ref<[id: string, state: HoverState, prev: HoverState][]>([])
  const featureState = useMapboxFeatureState()

  const addHoveredItem = (id: string, state: HoverState) => {
    const itemIndex = hoveredItems.value.findIndex((x) => x[0] === id)
    const prev = featureState.states[id]
    if (itemIndex !== -1) {
      hoveredItems.value[itemIndex] = [id, state, prev]
    } else {
      hoveredItems.value.push([id, state, prev])
    }
    setFeatureState(id, state)
  }

  const removeHoveredIds = (ids: string[]) => {
    ids.forEach((id) => {
      const itemIndex = hoveredItems.value.findIndex((x) => x[0] === id)
      if (itemIndex !== -1) {
        const [id, state, prev] = hoveredItems.value[itemIndex]
        removeFeatureState(id, state)
        if (prev) {
          setFeatureState(id, prev)
        }
        hoveredItems.value.splice(itemIndex, 1)
      }
    })
  }

  const removeLastHoveredItem = (): [id: string, state: HoverState] | undefined => {
    const lastItem = hoveredItems.value.pop()
    if (lastItem?.[0]) {
      const [id, state, prev] = lastItem
      removeFeatureState(id, state)
      if (prev) {
        setFeatureState(id, prev)
      }
      return [id, state]
    }
  }

  const clear = () => {
    hoveredItems.value.forEach(() => {
      const lastItem = hoveredItems.value.pop()
      if (lastItem?.[0]) {
        removeFeatureState(lastItem[0], lastItem[1])
      }
    })
  }

  function setFeatureState(id: string, state: HoverState) {
    if (!map) return
    const layers = getActiveLayers(map)
    const renderedFeatures = map.queryRenderedFeatures(undefined, {
      layers
    })
    const renderedFeatureCandidates = renderedFeatures.filter((f) => f.properties?.id === id)
    if (renderedFeatureCandidates.length > 0) {
      renderedFeatureCandidates.forEach((f) => {
        try {
          map.setFeatureState(f, state)
        } catch (e) {
          console.error(e)
        }
      })
      map.triggerRepaint()
    }
  }

  function removeFeatureState(id: string, state: HoverState) {
    if (!map) return
    const renderedFeatures = map.queryRenderedFeatures()
    const renderedFeature = renderedFeatures.find((f) => f.properties?.id === id)
    if (!renderedFeature) return
    Object.keys(state).forEach((key) => map.removeFeatureState(renderedFeature, key))
    map.triggerRepaint()
  }

  return {
    hoveredItems,
    addHoveredItem,
    removeLastHoveredItem,
    clear,
    removeHoveredIds
  }
})
